import { useState } from "react";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { useError } from "./useError";
import { useLoading } from "./useLoading";

// TODO: Refactor with SWR for server side mutation requests (https://swr.vercel.app/docs/mutation)
interface WriteConfig {
  method: HTTPRequestMethod;
  url: string;
  body?: unknown;
}

interface Props {
  onWriteFailure?: (e: Error) => void;
  onWriteFinally?: VoidFunction;
  unauthenticatedRequest?: boolean;
  errorMessage: string;
  writeConfig: WriteConfig;
  errorMessageKey?: string; // on error, use this key to get the error message from the response.
}

export type WriterComponentState = ReturnType<typeof useWriterComponentState>;

export function useWriterComponentState(props: Props) {
  const {
    onWriteFailure,
    onWriteFinally,
    unauthenticatedRequest = false,
    errorMessage,
    writeConfig,
    errorMessageKey,
  } = props;

  const { api, unauthenticatedApi } = useAPI();
  const { error, setError } = useError();
  const { loading, setLoading } = useLoading(false);
  const [success, setSuccess] = useState<boolean>(false);

  const resetState = () => {
    setError(null);
    setLoading(false);
    setSuccess(false);
  };

  function write<T>(
    onWriteSuccess?: (responseData: T) => void,
    // Overrides writeConfig.body for this call. Lets callers decide the body at
    // action time (e.g. per-toggle) without stashing it in a ref/mutable object.
    bodyOverride?: unknown,
  ) {
    setError(null);
    setLoading(true);

    const request = unauthenticatedRequest ? unauthenticatedApi : api;
    const body = bodyOverride !== undefined ? bodyOverride : writeConfig.body;

    request<T>(writeConfig.method, writeConfig.url, body)
      .then((response) => {
        setSuccess(true);
        setError(null);
        if (onWriteSuccess) {
          onWriteSuccess(response.data);
        }
      })
      .catch((e) => {
        setError(
          extractAPIRequestErrorMessage(e, errorMessage, errorMessageKey),
        );
        setSuccess(false);
        if (onWriteFailure) {
          onWriteFailure(e);
        }
      })
      .finally(() => {
        setLoading(false);
        if (onWriteFinally) {
          onWriteFinally();
        }
      });
  }

  return {
    loading,
    error,
    success,
    write,
    resetState,
  };
}
